import React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

const Pagination = ({
	page,
	perPage,
	total,
	onChange,
	className = 'blogroll-pagination',
	topRef,
}: any) => {
	const totalPages = Math.ceil(total / perPage);
	const { t } = useTranslation();

	if (isNaN(totalPages) || totalPages <= 1 || page === 'all') {
		return null;
	}

	const pageNumbers = [];

	for (let p = 1; p <= totalPages; p++) {
		pageNumbers.push(p);
	}

	const scrollToTop = () => {
		scrollTo({
			top: topRef.current.offsetTop - 32, // go to top of search results -2rem to account for possible overlap with sticky header
		});
	};

	const goToPage = (page) => {
		onChange(isNaN(page) ? 1 : parseInt(page, 10));
		scrollToTop();
	};

	const btnGen = (pageNum, active = false) => (
		<button
			type="button"
			aria-label={t(`Page ${pageNum}`)}
			className={cn('btn', active && 'btn-primary')}
			onClick={() => {
				goToPage(pageNum);
				scrollToTop();
			}}
		>
			{pageNum}
		</button>
	);

	let startingPageBtns = [];
	let endingPageBtns = [];

	// slightly complicated conditional for deciding what page numbers to display depending on active page.
	// only applies to totalPages > 7 since pagination shows first 3 pages + last 3 pages + ellipses.
	if (totalPages > 7) {
		// always create buttons for the first and last 3 pages
		for (let i = 1; i <= 3; i++) {
			startingPageBtns.push(btnGen(i, page === i));
		}

		for (let i = totalPages - 2; i <= totalPages; i++) {
			endingPageBtns.push(btnGen(i, page === i));
		}

		if (page >= 3 && page <= totalPages - 2) {
			if (page <= Math.floor(totalPages / 2)) {
				// if active page is in the FIRST half of total pages change the starting page button
				startingPageBtns = [
					btnGen(page - 2),
					btnGen(page - 1),
					btnGen(page, true),
				];
			} else {
				// if active page is closer to the SECOND falf of total pages change the ending page buttons
				endingPageBtns = [
					btnGen(page, true),
					btnGen(page + 1),
					btnGen(page + 2),
				];
			}
		}
	} else {
		startingPageBtns = null;
		for (let i = 1; i <= totalPages; i++) {
			endingPageBtns.push(btnGen(i, page === i));
		}
	}

	return (
		<nav className={className}>

			<div className="navigation-buttons">
				<button
					type="button"
					disabled={page === 1}
					aria-label={t('First')}
					className={cn('btn', page !== 1 && 'btn-primary')}
					onClick={() => {
						onChange(1);
						scrollToTop();
					}}
				>
					<i className="icon-caret-left-double" />
				</button>
				<button
					type="button"
					disabled={page === 1}
					aria-label={t('Previous')}
					className={cn('btn', page !== 1 && 'btn-primary')}
					onClick={() => {
						onChange(page - 1);
						scrollToTop();
					}}
				>
					<i className="icon-caret-left" />
				</button>
				<div className="pages d-none d-md-flex">
					{startingPageBtns && startingPageBtns}
					{startingPageBtns && <span className="ellipses">...</span>}
					{endingPageBtns}
				</div>
				<div className="page-counter d-flex d-md-none align-items-center">
					{page} {t('of')} {totalPages}
				</div>
				<button
					type="button"
					disabled={page === totalPages}
					aria-label={t('Next')}
					className={cn('btn', page !== totalPages && 'btn-primary')}
					onClick={() => {
						onChange(page + 1);
						scrollToTop();
					}}
				>
					<i className="icon-caret-right" />
				</button>
				<button
					type="button"
					disabled={page === totalPages}
					aria-label={t('Last')}
					className={cn('btn', page !== totalPages && 'btn-primary')}
					onClick={() => {
						onChange(totalPages);
						scrollToTop();
					}}
				>
					<i className="icon-caret-right-double" />
				</button>
			</div>
		</nav>
	);
};

export default Pagination;
